import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { profileAPI, authAPI, foodPreferencesAPI } from '../../utils/api';
import useUserStore from '../../store/userStore';
import { toast } from 'sonner';
import { Loader2, Utensils, ChevronLeft } from 'lucide-react';
import { trackOnboardingComplete } from '@/lib/posthog';
import useKeyboardAware from '../../hooks/useKeyboardAware';
import { addBreadcrumb, captureException } from '@/lib/sentry';
import FoodPreferencePicker from '../../components/FoodPreferencePicker';
import {
  addItem, draftFromPayload, legacyArrays, removeItem, removeUnresolved, restoreOnboardingDraft,
  toOnboardingPreferences,
} from '../../utils/foodPreferencesDraft';

const Allergens = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setProfile, setUser, profile, user } = useUserStore();
  useKeyboardAware();
  // Food preferences for this step: canonical groups/concepts only (the same
  // picker and backend authority as Settings). The draft is kept in the store
  // profile as `food_preferences_draft` on every change, so Back / Forward
  // (and a reload) keep every pill; completion persists it.
  const [groups, setGroups] = useState([]);
  const [draft, setDraft] = useState(() => (
    profile && profile.food_preferences_draft ? restoreOnboardingDraft(profile, []) : null
  ));
  const [loading, setLoading] = useState(false);

  // Derive auth state from user presence
  const isAuthenticated = !!user;
  const isDemoMode = user?.user_id?.startsWith('demo_');

  useEffect(() => {
    let alive = true;
    (async () => {
      let loadedGroups = [];
      try {
        const resp = await foodPreferencesAPI.getOptions();
        loadedGroups = (resp.data && resp.data.groups) || [];
      } catch (e) { /* the picker still searches; quick chips stay empty */ }
      if (!alive) return;
      setGroups(loadedGroups);
      if (draft) return;
      const current = useUserStore.getState().profile;
      if (isAuthenticated && !isDemoMode) {
        try {
          const resp = await foodPreferencesAPI.get();
          if (alive) setDraft(draftFromPayload(resp.data));
          return;
        } catch (e) { /* fall back to the locally known profile */ }
      }
      if (!alive) return;
      // Offline fallback: only recognized groups from locally known legacy
      // arrays. Never-saved free text is not restored, because the server
      // (correctly) refuses new unrecognized values.
      const restored = restoreOnboardingDraft(current, loadedGroups);
      setDraft({ ...restored, unresolved: { allergens: [], dislikes: [] } });
    })();
    return () => { alive = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateDraft = (next) => {
    setDraft(next);
    const current = useUserStore.getState().profile || {};
    setProfile({ ...current, food_preferences_draft: next });
  };

  const addPref = (list, item) => { if (draft) updateDraft(addItem(draft, list, item)); };
  const removePref = (list, key) => { if (draft) updateDraft(removeItem(draft, list, key)); };
  const removeUnresolvedPref = (list, raw) => { if (draft) updateDraft(removeUnresolved(draft, list, raw)); };

  /**
   * Complete onboarding - this is now the FINAL step
   * Saves all profile data to backend via consolidated /api/onboarding endpoint
   */
  const handleComplete = async () => {
    if (!draft) return;
    setLoading(true);
    const prefArrays = legacyArrays(draft);
    try {
      if (isAuthenticated && !isDemoMode) {
        // Collect all onboarding data from profile state + allergens/dislikes from this step
        const onboardingData = {
          // Personal Info - send both formats for compatibility
          gender: profile?.gender || profile?.sex,
          sex: profile?.sex || profile?.gender,
          age: profile?.age,
          
          // Height - send both metric and imperial
          height_cm: profile?.height_cm || profile?.height,
          height_ft: profile?.height_ft,
          height_in: profile?.height_in,
          
          // Weight - send both metric and imperial
          weight_kg: profile?.weight_kg || profile?.weight,
          weight_lbs: profile?.weight_lbs,
          goal_weight_lbs: profile?.goal_weight_lbs,
          
          // Goal
          goal: profile?.goal || profile?.display_goal,
          
          // Activity - send both field names
          activity_level: profile?.activity_level,
          workouts: profile?.workouts || profile?.workout_types,
          workout_types: profile?.workout_types || profile?.workouts,
          
          // Macros - send both naming conventions
          calories: profile?.calories || profile?.target_calories,
          protein: profile?.protein || profile?.target_protein_g,
          carbs: profile?.carbs || profile?.target_carbs_g,
          fat: profile?.fat || profile?.target_fat_g,
          target_calories: profile?.target_calories || profile?.calories,
          target_protein_g: profile?.target_protein_g || profile?.protein,
          target_carbs_g: profile?.target_carbs_g || profile?.carbs,
          target_fat_g: profile?.target_fat_g || profile?.fat,
          
          // Preferences - from this step: the canonical structure, plus the
          // legacy arrays so an older backend still receives them.
          food_preferences: toOnboardingPreferences(draft),
          allergies: prefArrays.allergens,
          allergens: prefArrays.allergens,
          dislikes: prefArrays.dislikes,
          diet_type: profile?.diet_type,
          
          // Cooking
          cookware: profile?.cookware,
          max_cook_time: profile?.max_cook_time,
        };

        console.log('[Onboarding] Saving all data to backend...', onboardingData);

        // [ONBOARDING_TRACE] Sentry breadcrumb — payload key shape only, no values.
        // Lets us see in TestFlight Sentry which fields the device sent.
        addBreadcrumb('onboarding', 'complete:1.before-save', {
          present_keys: Object.keys(onboardingData).filter((k) => onboardingData[k] !== undefined && onboardingData[k] !== null),
          missing_keys: Object.keys(onboardingData).filter((k) => onboardingData[k] === undefined || onboardingData[k] === null),
        });

        // Save all onboarding data to backend
        const response = await profileAPI.completeOnboarding(onboardingData);

        addBreadcrumb('onboarding', 'complete:2.save-response', {
          status: response?.status,
          success: response?.data?.success,
          onboarding_complete: response?.data?.onboarding_complete,
        });

        if (!response.data.success) {
          throw new Error('Failed to save onboarding data');
        }

        // Verify by calling /api/auth/me
        console.log('[Onboarding] Verifying with /api/auth/me...');
        const authResponse = await authAPI.getMe();

        addBreadcrumb('onboarding', 'complete:3.verify-response', {
          status: authResponse?.status,
          onboarding_complete: authResponse?.data?.onboarding_complete,
          has_user_id: !!authResponse?.data?.user_id,
        });

        if (!authResponse.data.onboarding_complete) {
          throw new Error('Onboarding not marked complete in database');
        }

        // Update local store with verified data
        setUser(authResponse.data);
        if (response.data.profile) {
          setProfile(response.data.profile);
        }

        // Track completion
        trackOnboardingComplete();
        
        // Navigate to home
        console.log('[Onboarding] Complete! Navigating to /home');
        toast.success('Setup complete! Let\'s plan your first meal.');
        navigate('/home', { replace: true });
        
      } else if (isDemoMode) {
        // Demo mode - store locally only
        const { food_preferences_draft: _draft, ...rest } = useUserStore.getState().profile || {};
        setProfile({ ...rest, allergens: prefArrays.allergens, allergies: prefArrays.allergens,
          dislikes: prefArrays.dislikes });
        setUser({ ...user, onboarding_complete: true });
        toast.success('Setup complete!');
        navigate('/home', { replace: true });
        
      } else {
        // Not logged in - this shouldn't happen as OnboardingRoute requires auth
        toast.error('Please log in to complete setup');
        navigate('/login', { replace: true });
      }
    } catch (error) {
      console.error('[Onboarding] Error:', error);
      // [ONBOARDING_TRACE] Send full structured failure to Sentry with the
      // step we were on, the HTTP status if available, and the response body.
      // This is the diagnostic that surfaces in production Sentry from TestFlight.
      const status = error?.response?.status;
      const responseData = error?.response?.data;
      const responseDataStr = (() => {
        try {
          return JSON.stringify(responseData).slice(0, 500);
        } catch (_e) {
          return String(responseData);
        }
      })();
      captureException(error, {
        tags: {
          flow: 'onboarding',
          step: 'allergens.handleComplete',
          http_status: String(status || 'none'),
        },
        extra: {
          message: error?.message,
          response_status: status,
          response_data_preview: responseDataStr,
          // Pydantic 422 errors include `detail: [{loc, msg, type}]` — surface it.
          validation_detail: responseData?.detail,
        },
      });
      toast.error('Failed to complete setup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen header-safe-top px-6 pb-24">
      <motion.div
        initial={{ opacity: 0, y: 0 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md mx-auto space-y-8"
      >
        <button
          onClick={() => {
            // The draft is already in the store (food_preferences_draft).
            if (location.state?.mode === 'manual') navigate('/onboarding/manual-macros');
            else navigate('/onboarding/quiz', { state: { returnToStep: 4 } });
          }}
          data-testid="allergens-back-btn"
          className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronLeft size={18} /> Back
        </button>

        <div className="text-center space-y-2">
          <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center mx-auto">
            <Utensils size={24} className="text-accent" />
          </div>
          <h1 className="font-heading font-bold text-2xl" data-testid="allergens-title">Food Preferences</h1>
          <p className="text-muted-foreground text-sm">Help us personalize your meal recommendations</p>
        </div>

        <div className="space-y-8">
          {/* Allergens Section */}
          <div className="space-y-4">
            <h2 className="font-heading font-semibold text-lg">Allergens</h2>
            {!draft ? (
              <p className="text-sm text-muted-foreground" data-testid="preferences-loading">Loading…</p>
            ) : (
              <FoodPreferencePicker
                purpose="allergen"
                items={draft.allergens}
                unresolved={draft.unresolved.allergens}
                groups={groups}
                disabled={loading}
                testIdPrefix="allergen"
                onAdd={(item) => addPref('allergens', item)}
                onRemove={(key) => removePref('allergens', key)}
                onRemoveUnresolved={(raw) => removeUnresolvedPref('allergens', raw)}
              />
            )}
          </div>

          {/* Dislikes Section */}
          <div className="space-y-4">
            <h2 className="font-heading font-semibold text-lg">Dislikes</h2>
            {draft && (
              <FoodPreferencePicker
                purpose="dislike"
                items={draft.dislikes}
                unresolved={draft.unresolved.dislikes}
                groups={groups}
                disabled={loading}
                testIdPrefix="dislike"
                onAdd={(item) => addPref('dislikes', item)}
                onRemove={(key) => removePref('dislikes', key)}
                onRemoveUnresolved={(raw) => removeUnresolvedPref('dislikes', raw)}
              />
            )}
          </div>
        </div>

        <p className="text-xs text-muted-foreground leading-relaxed text-center px-2" data-testid="allergen-disclaimer">
          Food preferences and allergen selections help filter meal suggestions, but Fitxess does not guarantee meals are allergen-free. Always review ingredient labels, restaurant information, and packaging before eating.
        </p>

        <Button
          onClick={handleComplete}
          data-testid="complete-onboarding-btn"
          disabled={loading || !draft}
          className="w-full h-12 rounded-full"
        >
          {loading ? (
            <>
              <Loader2 size={20} className="animate-spin mr-2" />
              Completing Setup...
            </>
          ) : (
            'Complete Setup'
          )}
        </Button>
      </motion.div>
    </div>
  );
};

export default Allergens;
